// FilterDropdown — ds/Dropdown trigger + an options menu underneath.
//
// The ds Dropdown is presentational (it owns its chrome but expects the
// consumer to drive open/closed and render the menu). This thin wrapper
// supplies a Chakra-style popover with one option per row, including an
// "All <items>" reset row at the top.

import { Box, Icon, Tooltip } from "@chakra-ui/react";
import {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { TbCheck } from "react-icons/tb";
import Dropdown from "components/ds/Dropdown";

export interface FilterOption {
  id: string;
  label: string;
}

interface FilterDropdownProps {
  icon: ReactNode;
  label: string;
  /** Resolved option for the current `value`, or null = no filter. */
  value: string | null;
  options: FilterOption[];
  /** Text shown when value is null. Also drives the "All <items>" reset row label. */
  allLabel: string;
  onChange: (next: string | null) => void;
  /** Narrow-container mode: the split-pill trigger collapses to an icon-only
   *  button (tooltip carries the label + current selection). The ds Dropdown
   *  has no compact variant yet — DS gap, trigger is hand-rolled locally. */
  compact?: boolean;
  /** Anchor edge for the options menu. Compact triggers sit at the right end
   *  of a narrow row, so they right-anchor to stay inside the column. */
  menuAlign?: "left" | "right";
}

const FilterDropdown = ({
  icon,
  label,
  value,
  options,
  allLabel,
  onChange,
  compact = false,
  menuAlign = "left",
}: FilterDropdownProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Outside-click close — Chakra's Popover would also work but adds extra
  // chrome we don't want around the menu.
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const current = value ? options.find((o) => o.id === value) : null;

  const active = value !== null;

  return (
    <Box position="relative" ref={ref} flexShrink={0}>
      {compact ? (
        <Tooltip
          label={`${label}: ${current?.label ?? allLabel}`}
          placement="top"
          hasArrow
          openDelay={250}
          bg="neutral.950"
          color="neutral.0"
          fontSize="xs"
          px="10px"
          py="1xs"
          borderRadius="component.md"
        >
          <Box
            as="button"
            type="button"
            aria-label={`${label}: ${current?.label ?? allLabel}`}
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            w="40px"
            h="40px"
            flexShrink={0}
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="component.lg" // 8px — matches the ds Dropdown
            borderWidth="1px"
            borderStyle="solid"
            borderColor={open ? "neutral.300" : active ? "p.200" : "neutral.200"}
            bg={active ? "p.50" : "neutral.0"}
            color={active ? "p.600" : "neutral.700"}
            cursor="pointer"
            transition="background-color 120ms cubic-bezier(0.23, 1, 0.32, 1), border-color 120ms cubic-bezier(0.23, 1, 0.32, 1), color 120ms cubic-bezier(0.23, 1, 0.32, 1)"
            _hover={{
              borderColor: active ? "p.300" : "neutral.400",
            }}
            _focusVisible={{ outline: "none", boxShadow: "focusRing" }}
          >
            {icon}
          </Box>
        </Tooltip>
      ) : (
        <Dropdown
          icon={icon}
          label={label}
          value={current?.label}
          placeholder={allLabel}
          size="md"
          treatment="outline"
          open={open}
          onClick={() => setOpen((o) => !o)}
        />
      )}
      {open && (
        <MenuPanel align={menuAlign} minW={compact ? "220px" : "100%"}>
          <MenuRow
            label={allLabel}
            selected={value === null}
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
          />
          {options.map((o) => (
            <MenuRow
              key={o.id}
              label={o.label}
              selected={value === o.id}
              onClick={() => {
                onChange(o.id);
                setOpen(false);
              }}
            />
          ))}
        </MenuPanel>
      )}
    </Box>
  );
};

// Lifted out so we can drive the open-state scale-in via useLayoutEffect
// scoped to the panel — when `open` flips from false to true, MenuPanel
// mounts and animates from scale(0.97) to 1.
const MenuPanel = ({
  children,
  align = "left",
  minW = "100%",
}: {
  children: ReactNode;
  align?: "left" | "right";
  minW?: string;
}) => {
  const [mounted, setMounted] = useState(false);
  useLayoutEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <Box
      position="absolute"
      top="calc(100% + 6px)"
      left={align === "left" ? 0 : undefined}
      right={align === "right" ? 0 : undefined}
      minW={minW}
      maxH="320px"
      overflowY="auto"
      bg="neutral.0"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="neutral.200"
      borderRadius="component.xl" // 10px
      boxShadow="overlay"
      p="2xs"
      zIndex={60}
      // Scale-in from the trigger's anchored corner. Same playbook as
      // RecipientPicker — feels anchored, not floating in from nowhere.
      transformOrigin={align === "left" ? "top left" : "top right"}
      opacity={mounted ? 1 : 0}
      transform={mounted ? "scale(1)" : "scale(0.97)"}
      transition="opacity 150ms cubic-bezier(0.23, 1, 0.32, 1), transform 150ms cubic-bezier(0.23, 1, 0.32, 1)"
    >
      {children}
    </Box>
  );
};

const MenuRow = ({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) => (
  <Box
    as="button"
    type="button"
    onClick={onClick}
    display="flex"
    alignItems="center"
    gap="sm"
    w="full"
    px="10px"
    py="sm"
    borderRadius="component.md" // 6px
    fontSize="md"
    fontFamily="inherit"
    textAlign="left"
    bg={selected ? "p.50" : "transparent"}
    color="neutral.950"
    fontWeight={selected ? 600 : 400}
    border="none"
    cursor="pointer"
    transition="background-color 100ms cubic-bezier(0.23, 1, 0.32, 1), transform 100ms cubic-bezier(0.23, 1, 0.32, 1)"
    _hover={{ bg: selected ? "p.100" : "neutral.100" }}
    _active={{ transform: "scale(0.98)" }}
  >
    <Box flex={1}>{label}</Box>
    {selected && <Icon as={TbCheck} boxSize="14px" color="p.600" />}
  </Box>
);

export default FilterDropdown;
